import React, { useEffect, useState } from 'react';
import { Col, Container, Dropdown, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import UserDefinedField from '../../components/Fields/UserDefinedField';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import ProductCategoryModel from '../../models/ProductCategoryModel';
import UserDefinedFieldValueModel from '../../models/UserDefinedFieldValueModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { Result } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';
import EditProductCategoryReferencesPopup from './Popups/ProductCategory_popup_EditReferences';
import ProductCategory_popup_export from './Popups/ProductCategory_popup_export';
import ProductCategory_tab_Employees from './Tabs/ProductCategory_tab_Employees';
import ProductCategory_tab_Items from './Tabs/ProductCategory_tab_Items';
import ProductCategory_tab_SubCategories from './Tabs/ProductCategory_tab_SubCategories';

const ProductCategoryView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [showEditReferencesPopup, setShowEditReferencesPopup] = useState(false)
    const [showExportReferencesPopup, setShowExportReferencesPopup] = useState(false)
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const navigate = useNavigate()

    const [formData, setFormData] = useState<ProductCategoryModel>({
        id: 0,
        name: '',
        abbreviation: '',
        itemTypeId: 0,
        parentProductCategoryId: 0,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        concurrencyId: '',
        userDefinedFields: [],
    });

    const productCategories = useSelectListOptions('api/v1/selectlist/ProductCategoryNameTrees?activeObly=true', '-- Select category');
    const itemTypes = useSelectListOptions('api/v1/selectlist/ItemTypes?activeOnly=true', '-- Select itemtype');

    let idParams: IdParams = useParams();
    let productCategoryId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        productCategoryId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (productCategoryId !== undefined) {
            DataService.getById<ProductCategoryModel>('api/v1/productcategories/', productCategoryId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [productCategoryId]);

    function submit() {
        if (formData) {
            DataService
                .update<ProductCategoryModel>(`api/v1/productcategories/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        navigate('/plm/itemsetup/productcategories')
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
            { name: 'abbreviation', label: 'Abbreviation', labelSize: 3 },
            { name: 'itemTypeId', label: 'Item type', labelSize: 3 },
            { name: 'parentProductCategoryId', label: 'Parent product category', labelSize: 3 },
            { name: 'beginDate', label: 'Active from', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    if (formData && formData.userDefinedFields) {
        formData.userDefinedFields.forEach((value, index) => {
            let label = value.userDefinedField.name
            formConfig.fields.push({ name: 'userDefinedFields.' + index + '.value', label: label, labelSize: 3 });
        });
    }

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (productCategoryId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditProductCategory)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem width={230} icon="fa fa-image" title="Change logo" appFunction={ApplicationFunctions.EditProductCategory} disabled />
            <ActionMenuItem icon="fa fa-edit" title="Change reference codes" appFunction={ApplicationFunctions.EditProductCategory} onClick={() => setShowEditReferencesPopup(true)} />
            <Dropdown.Divider />
            <ActionMenuItem icon="fa fa-sign-out" title="Export category" appFunction={ApplicationFunctions.EditProductCategory} onClick={() => setShowExportReferencesPopup(true)} />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Product category' actions={formActions} footer={(
                <>
                    {formData.thumbnailUrl && (
                        <Row>
                            <Col md='2'>
                                <ButtonBar offset={0}>
                                </ButtonBar>
                            </Col>
                            <Col md='10'>
                                <ButtonBar offset={3}>
                                    <Authorize applicationFunction={ApplicationFunctions.EditProductCategory}>
                                        <SaveButton  {...getSubmitButtonProps()} />
                                    </Authorize>
                                </ButtonBar>
                            </Col>
                        </Row>
                    )}
                    {!formData.thumbnailUrl && (
                        <Row>
                            <Col md='10'>
                                <ButtonBar offset={3}>
                                    <Authorize applicationFunction={ApplicationFunctions.EditProductCategory}>
                                        <SaveButton  {...getSubmitButtonProps()} />
                                    </Authorize>
                                </ButtonBar>
                            </Col>
                            <Col md='2'>
                                <ButtonBar offset={0}>
                                </ButtonBar>
                            </Col>
                        </Row>
                    )}
                </>
            )}>
                <Container fluid>
                    <Row>
                        {formData.thumbnailUrl && (
                            <Col md='2'>
                                <img src={formData.thumbnailUrl} style={{ width: '100%' }} />
                            </Col>
                        )}
                        <Col md='10'>
                            <TextboxField {...getFieldProps('name')} />
                            <TextboxField {...getFieldProps('abbreviation')} />
                            <SelectListField {...getFieldProps('itemTypeId')} options={itemTypes} />
                            <SelectListField {...getFieldProps('parentProductCategoryId')} options={productCategories} />
                            {formData && formData.userDefinedFields &&
                                formData.userDefinedFields.map((value, index) => {
                                    let name = 'userDefinedFields.' + index + '.value';
                                    return (<UserDefinedField {...getFieldProps(name)} userDefinedFieldValue={value} key={'userDefinedField' + index} />);
                                })
                            }
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                </Container>
            </Form>
            {productCategoryId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='vtabs'
                                defaultActiveKey='subCategories'
                                tabs={[
                                    {
                                        title: 'Sub-Categories',
                                        eventKey: 'subCategories',
                                        component: <ProductCategory_tab_SubCategories id={productCategoryId} />,
                                    },
                                    {
                                        title: 'Items',
                                        eventKey: 'items',
                                        component: <ProductCategory_tab_Items id={productCategoryId} />,
                                    },
                                    {
                                        title: 'Employees',
                                        eventKey: 'employees',
                                        component: <ProductCategory_tab_Employees id={productCategoryId} />,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
            {showEditReferencesPopup && <EditProductCategoryReferencesPopup productCategory={formData} show={showEditReferencesPopup} onHide={() => setShowEditReferencesPopup(false)} />}
            {showExportReferencesPopup && <ProductCategory_popup_export productCategory={formData} show={showExportReferencesPopup} onHide={() => setShowExportReferencesPopup(false)} />}
        </div>
    );
}
export default ProductCategoryView;